<template>
        <div class="search__wrapper">
            <div class="search__input-wrapper">
                <input
                    id="query"
                    ref="searchInput"
                    v-model="query"
                    class="search__input"
                    type="text"
                    data-type="query"
                    :data-keydown="idBottomSearchInput"
                    :placeholder="enterSearchText" 
                    :class="{'search__input_reduced': isFrench}" 
                    @focusin="onFocus"
                    @focusout="onFocusout"
                >
                <common-icon :icon-name="'IconSearch'" class="search__icon" />
            </div>
            <ul v-if="isVisibleSuggestions" class="search-suggestions">
                <li v-for="(word, index) in getPopularWords" :key="word" class="search-suggestions__suggestion">
                    <button     
                        :id="generateId(index)"
                        type="button" 
                        tabindex="0" 
                        class="search-suggestions__suggestion-btn"
                        data-keyup="query"
                        :data-keyleft="getIdLeft(index)"
                        :data-keyright="getIdRight(index)"
                        @focusin="onFocusBtn"
                        @focusout="onFocusoutBtn"
                        @click="selectSuggestion(word)"
                    >
                        {{ word }}
                    </button>
                </li>
            </ul>
        </div>
</template>

<script lang="ts" setup>
    import { Strings } from '@/locales/types/enums';
    import { useI18n } from "vue-i18n";
    import { linkItems } from "@/components/Header/types";
    import { useRouter } from "vue-router";
    import { storeToRefs } from 'pinia';
    import { useSearchPageStore } from "@/stores/SearchPage";
    import { useFocus } from "@/composables/useFocus";
    import { LangCodes } from "@/common/types/languages";

    const { focusToHeader } = useFocus();

    const SearchPageStore = useSearchPageStore();
    const {
        getQuery,
        getPopularWords
    } = storeToRefs(SearchPageStore);
    const { fetchPopularWords } = SearchPageStore;

    const { $navigator } = useNuxtApp();
    const router = useRouter();
    const { t, locale } = useI18n();
    

    const searchInput: Ref<HTMLInputElement| null> = ref(null);
    const isFrench: boolean = locale.value === LangCodes.FR;
    const enterSearchText: string = t(Strings.SEARCH_ENTER_TEXT).toString();
    const query: Ref<string> = ref(getQuery.value);
    const debounce: Ref<ReturnType<typeof setTimeout> | null> = ref(null);

    const generateId = (idx: number) => {
        return `word-${idx}`;
    };
    
    const getIdLeft = (idx: number): string | undefined => {
        if (idx === 0) {
            return undefined;
        }
        
        return generateId(idx - 1);
    };
    const getIdRight = (idx: number): string | undefined => {

        if (idx === getPopularWords.value.length - 1) {
            return undefined;
        }
        return generateId(idx + 1);
    };
    const onFocusBtn = () => {
        registerKeyListeners();
    };
    const onFocusoutBtn = () => {
        unregisterKeyListeners();
    }
    const onFocus = () => {
        visibilitySuggestions.value = true;
        window.scrollTo(0, 0);
        $navigator.disableLeftAndRightButtons(); //it`is work in TV but not browser. It's normal
        registerKeyListeners();
    };
    const onFocusout = () => {
        $navigator.enableLeftAndRightButtons(); //it`is work in TV but not browser. It's normal
        unregisterKeyListeners();

    }
    const focusSearchResult = inject<() => void>('focusSearchResult');

    const focusHeader = () =>{
        hideSuggestions();
        focusToHeader();
    };
    const focusResult = () =>{
        hideSuggestions();
        if (focusSearchResult) {
            focusSearchResult();
        } 
    };

    const registerKeyListeners = () => {
        $navigator.on("keypress:back", focusHeader);
        $navigator.on("keypress:up", focusHeader);
        $navigator.on("keypress:down", focusResult);
    }
    const unregisterKeyListeners = () => {
        $navigator.off("keypress:back", focusHeader);
        $navigator.off("keypress:up", focusHeader);
        $navigator.off("keypress:down", focusResult);
    }

    const idBottomSearchInput = computed((): string | undefined => isVisibleSuggestions.value ? generateId(0) : undefined);

    const visibilitySuggestions: Ref<boolean> = ref(false);
    const hideSuggestions = (): void => {
        visibilitySuggestions.value = false;
    };
    const isVisibleSuggestions = computed(() => visibilitySuggestions.value && getPopularWords.value.length > 0 );
    const selectSuggestion = (word: string) => {
        query.value = word;
        searchInput.value?.focus();
    };
    watch(
        () => query.value,
        (value: string) => {
            if (value === getQuery.value) {
                return;
            }

            if (debounce.value !== null) {
                clearTimeout(debounce.value);
            }

            void fetchPopularWords(value);

            if (value.length === 0) {
                router.replace({ path: linkItems.search.routerPath}).catch((e)=>{
                    Logger.captureError(e as Error);
                });
                return;
            }

            debounce.value = setTimeout(() => {
                router.replace({ path: linkItems.search.routerPath, query: { value }}).catch((e)=>{
                    Logger.captureError(e as Error);
                });
            }, 1500);
        }
    );
</script>
